/**
 * Security Signals Page (Review 005)
 *
 * Repositioned from standalone scanner dashboard to governance signal layer.
 * Scanners are signal sources feeding the governance control plane.
 *
 * Route: /scanners | Nav: Engineering > Security Signals
 * Category: Code (#E852B9)
 */
import React, { useMemo } from 'react';
import {
  Title2,
  Body1,
  Body2,
  Card,
  CardHeader,
  Badge,
  tokens,
  Spinner,
} from '@fluentui/react-components';
import {
  ScanDashRegular,
  CodeRegular,
  BoxRegular,
  LockClosedRegular,
  CloudRegular,
  ShieldRegular,
} from '@fluentui/react-icons';
import { useQuery } from '@tanstack/react-query';
import { runsApi } from '../services/api';
import { useDashboardOverview } from '../hooks/useDashboardOverview';
import { usePageStyles } from '../styles/page-styles';
import { UnauthenticatedBanner } from '../components/common/UnauthenticatedBanner';
import { PageHeader } from '../components/common/PageHeader';
import { EmptyStateCard } from '../components/common/EmptyStateCard';
import { GovernancePipelineFlow, type PipelineStep } from '../components/design-system';
import { governanceTokens } from '../components/design-system';
import type { Run } from '../types';

const CATEGORY_COLOR = '#E852B9';

interface SignalInfo {
  key: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  enabledField: keyof Run;
  linkedControls: string[];
}

const SIGNALS: SignalInfo[] = [
  { key: 'sast', name: 'SAST', description: 'Static Application Security Testing', icon: <CodeRegular />, enabledField: 'sastEnabled', linkedControls: ['AC-2', 'SI-10'] },
  { key: 'sca', name: 'SCA', description: 'Software Composition Analysis', icon: <BoxRegular />, enabledField: 'scaEnabled', linkedControls: ['SC-7', 'SI-2'] },
  { key: 'secrets', name: 'Secrets', description: 'Secret & Credential Detection', icon: <LockClosedRegular />, enabledField: 'secretsEnabled', linkedControls: ['AC-2', 'IA-5'] },
  { key: 'iac', name: 'IaC', description: 'Infrastructure as Code Scanning', icon: <CloudRegular />, enabledField: 'iacEnabled', linkedControls: ['CM-6', 'SC-28'] },
  { key: 'container', name: 'Container', description: 'Container Image Analysis', icon: <ShieldRegular />, enabledField: 'containerEnabled', linkedControls: ['SI-3', 'CM-7'] },
];

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export const ScannersPage: React.FC = () => {
  const ps = usePageStyles();

  const { data: runs, isLoading, error: runsError } = useQuery({
    queryKey: ['runs', 'recent', 20],
    queryFn: () => runsApi.getRecent(20),
    retry: 1,
  });

  const { data: dashboardData } = useDashboardOverview();

  const signalStats = useMemo(() => {
    if (!runs) return null;
    return SIGNALS.map((signal) => {
      const matchingRuns = runs.filter((r) => r[signal.enabledField] as boolean);
      const lastRun = matchingRuns.find((r) => r.completedAt);
      const totalFindings = matchingRuns.reduce((sum, r) => sum + r.totalFindings, 0);
      const policiesTriggered = matchingRuns.filter((r) => r.totalFindings > 0).length;
      return {
        ...signal,
        scanCount: matchingRuns.length,
        lastSignal: lastRun?.completedAt ?? null,
        active: matchingRuns.length > 0,
        totalFindings,
        policiesTriggered,
      };
    });
  }, [runs]);

  const recentEvents = useMemo(() => {
    if (!runs) return [];
    return runs.slice(0, 10).map((run) => {
      const enabledSignals = SIGNALS.filter((s) => run[s.enabledField] as boolean).map((s) => s.name);
      return { ...run, enabledSignals };
    });
  }, [runs]);

  // Governance impact metrics
  const todayRuns = useMemo(() => {
    if (!runs) return [];
    const today = new Date().toDateString();
    return runs.filter(r => new Date(r.createdAt).toDateString() === today);
  }, [runs]);

  const signalsToday = todayRuns.reduce((sum, r) => sum + r.totalFindings, 0);
  const policiesTriggeredToday = dashboardData?.enforcement?.blockedCount ?? 0 + (dashboardData?.enforcement?.warnedCount ?? 0);
  const evidenceCreated = dashboardData?.evidenceChain?.totalEvaluations ?? 0;

  // Signal flow pipeline steps
  const signalFlowSteps: PipelineStep[] = [
    { id: 'signals', label: 'Security Signals', status: (signalStats?.some(s => s.active) ? 'success' : 'inactive'), metadata: `${signalStats?.filter(s => s.active).length ?? 0} active` },
    { id: 'evaluation', label: 'Policy Evaluation', status: (dashboardData?.enforcement?.totalEvaluations ? 'success' : 'inactive'), metadata: `${dashboardData?.enforcement?.totalEvaluations ?? 0} evals` },
    { id: 'enforcement', label: 'Enforcement', status: ((dashboardData?.enforcement?.blockedCount ?? 0) > 0 ? 'warning' : 'success'), metadata: `${dashboardData?.enforcement?.blockedCount ?? 0} blocked` },
    { id: 'evidence', label: 'Evidence Created', status: (evidenceCreated > 0 ? 'success' : 'inactive'), metadata: `${evidenceCreated} records` },
  ];

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: tokens.spacingVerticalXXXL }}>
        <Spinner size="large" label="Loading signal data..." />
      </div>
    );
  }

  // Auth error detection
  const queryError = runsError as any;
  const isUnauth = queryError?.status === 401 || queryError?.message?.includes('MSAL');
  if (isUnauth) {
    return (
      <div className={ps.page}>
        <PageHeader title="Security Signals Feeding Enforcement" subtitle="Signals are inputs. What matters is the enforcement decisions they trigger." icon={<ScanDashRegular />} iconColor={CATEGORY_COLOR} showAIButton={false} />
        <UnauthenticatedBanner feature="signal data" />
      </div>
    );
  }

  return (
    <div className={ps.page}>
      <PageHeader title="Security Signals Feeding Enforcement" subtitle="Signals are inputs. What matters is the enforcement decisions they trigger." icon={<ScanDashRegular />} iconColor={CATEGORY_COLOR} />

      {/* Signal Flow Visualization (F085) */}
      <Card>
        <CardHeader header={<Title2>Signal Pipeline</Title2>} description="How security signals flow through the governance control plane" />
        <div className={ps.cardBody}>
          <GovernancePipelineFlow steps={signalFlowSteps} />
        </div>
      </Card>

      {/* Signal Source Cards (F082) */}
      <div className={ps.metricGrid}>
        {signalStats?.map((signal) => (
          <Card key={signal.key}>
            <div className={ps.cardBody}>
              <div className={ps.statTile}>
                <span style={{ fontSize: '1.5rem', color: signal.active ? CATEGORY_COLOR : tokens.colorNeutralForeground3 }}>
                  {signal.icon}
                </span>
                <Title2>{signal.name}</Title2>
                <Body2 style={{ color: tokens.colorNeutralForeground3 }}>{signal.description}</Body2>
                <div style={{ display: 'flex', justifyContent: 'center', gap: tokens.spacingHorizontalS, marginTop: tokens.spacingVerticalXS }}>
                  <Badge appearance="filled" color={signal.active ? 'success' : 'warning'}>
                    {signal.active ? 'Active' : 'Not Connected — Blind Spot'}
                  </Badge>
                </div>
                {signal.active && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, alignItems: 'center' }}>
                    <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                      Last signal: {signal.lastSignal ? timeAgo(signal.lastSignal) : '--'}
                    </Body2>
                    <div style={{ display: 'flex', gap: tokens.spacingHorizontalS }}>
                      <Badge appearance="outline" size="small">{signal.totalFindings} findings</Badge>
                      <Badge appearance="outline" size="small">{signal.policiesTriggered} policies</Badge>
                    </div>
                    <div style={{ display: 'flex', gap: tokens.spacingHorizontalXS, flexWrap: 'wrap', justifyContent: 'center' }}>
                      {signal.linkedControls.map(ctrl => (
                        <Badge key={ctrl} appearance="tint" size="small" color="informative">{ctrl}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Governance Impact (F084) */}
      <Card>
        <CardHeader
          header={<Title2>Governance Impact</Title2>}
          description="How security signals connect to governance outcomes"
        />
        <div className={ps.cardBody}>
          <div className={ps.metricGrid}>
            <div className={ps.statTile}>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Signals Today</Body2>
              <Title2>{signalsToday}</Title2>
            </div>
            <div className={ps.statTile}>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Policies Triggered</Body2>
              <Title2 style={{ color: policiesTriggeredToday > 0 ? governanceTokens.colorStatusWarning : undefined }}>{policiesTriggeredToday}</Title2>
            </div>
            <div className={ps.statTile}>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Controls Impacted</Body2>
              <Title2>{signalStats?.filter(s => s.active).reduce((sum, s) => sum + s.linkedControls.length, 0) ?? 0}</Title2>
            </div>
            <div className={ps.statTile}>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Evidence Created</Body2>
              <Title2 style={{ color: governanceTokens.colorStatusSuccess }}>{evidenceCreated}</Title2>
            </div>
          </div>
        </div>
      </Card>

      {/* Recent Signal Events (F086) */}
      <Card>
        <CardHeader
          header={<Title2>Recent Signal Events</Title2>}
          description="Latest security signals and their governance impact"
        />
        <div className={ps.cardBody}>
          {recentEvents.length > 0 ? (
            <div className={ps.rowList}>
              {recentEvents.map((run) => (
                <div key={run.id} className={ps.rowItem}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM, flex: 1 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS }}>
                      <Body1 style={{ fontWeight: 500 }}>{run.targetIdentifier || run.name}</Body1>
                      <div style={{ display: 'flex', gap: tokens.spacingHorizontalXS, flexWrap: 'wrap' }}>
                        {run.enabledSignals.map((s) => (
                          <Badge key={s} appearance="tint" size="small" style={{ backgroundColor: `${CATEGORY_COLOR}20`, color: CATEGORY_COLOR }}>
                            {s}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM }}>
                    <Badge appearance="outline" size="small">
                      {run.totalFindings} findings
                    </Badge>
                    <Badge
                      appearance="filled"
                      color={run.status === 'Completed' ? 'success' : run.status === 'Failed' ? 'danger' : 'warning'}
                      size="small"
                    >
                      {run.status}
                    </Badge>
                    <Body2 style={{ color: tokens.colorNeutralForeground3, whiteSpace: 'nowrap' }}>
                      {timeAgo(run.createdAt)}
                    </Body2>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyStateCard
              icon={<ScanDashRegular />}
              title="No Security Signals"
              description="No signal coverage active. Risks cannot be detected. Enforcement blind spots exist. Connect your first repository or pipeline to begin."
              accentColor={CATEGORY_COLOR}
              actionLabel="Add Integration"
              onAction={() => window.location.href = '/integrations'}
            />
          )}
        </div>
      </Card>
    </div>
  );
};
